import { OrganizationKeyName } from "./model/Organization";

export const routePatterns = {
  login: "/login",
  selectOrganization: "/select-organization",
  home: "/organizations/:organization",
  userList: "/organizations/:organization/users",
  userProfile: "/organizations/:organization/users/profile/:id",
  userProfileUserVoucher:
    "/organizations/:organization/users/profile/:id/user-voucher/:userVoucherId",
  userEventLogList: "/organizations/:organization/users/event-log/:id",
  venueList: "/organizations/:organization/venues",
  venueNew: "/organizations/:organization/venues/new",
  venueEdit: "/organizations/:organization/venues/:id/edit",
  menuList: "/organizations/:organization/menus",
  menuEdit: "/organizations/:organization/menus/:id",
  menuEditRecommendedProducts:
    "/organizations/:organization/menus/:id/recommended-products",
  menuEditMenuAvailability:
    "/organizations/:organization/menus/:id/menu-availability",
  menuEditCampaigns: "/organizations/:organization/menus/:id/campaigns",
  menuEditCampaignsAdd: "/organizations/:organization/menus/:id/campaigns/new",
  organizationUISettingsEdit: "/organizations/:organization/settings/edit",
  analytics: "/organizations/:organization/analytics",
  // Kept as a signpost to the new admin; the pages themselves are gone.
  reports: "/organizations/:organization/reports",
  reportDetails: "/organizations/:organization/reports/details/:slug",
  beaconStatus: "/organizations/:organization/beacon-status",
  beaconStatusView: "/organizations/:organization/beacon-status/:id/:idNfc",
  promotionCodeList: "/organizations/:organization/promotion-codes",
  promotionCodeAdd: "/organizations/:organization/promotion-codes/new",
  promotionCodeEdit: "/organizations/:organization/promotion-codes/:id/edit",
  organizationEventLog: "/organizations/:organization/event-log",
  emailTemplates: "/organizations/:organization/email-templates",
  emailTemplateEdit: "/organizations/:organization/email-templates/:id/edit",
  emailTemplateNew: "/organizations/:organization/email-templates/new",
  adminUserList: "/organizations/:organization/admin-users",
  adminUserAdd: "/organizations/:organization/admin-users/new",
  adminUserEdit: "/organizations/:organization/admin-users/:id/edit",
  memberCardTypes: "/organizations/:organization/member-card-types",
  memberCardTypeEdit: "/organizations/:organization/member-card-types/:id/edit",
  memberCardTypeNew: "/organizations/:organization/member-card-types/new",
  partnershipList: "/organizations/:organization/partnerships",
  partnershipAdd: "/organizations/:organization/partnerships/new",
  partnershipEdit: "/organizations/:organization/partnerships/:id/edit",
  shopList: "/organizations/:organization/shop",
  shopNew: "/organizations/:organization/shop/new",
  shopNewWithVenue: "/organizations/:organization/shop/new/with-venue/:venueId",
  shopEdit: "/organizations/:organization/shop/:id/edit",
  shopProductListForShop: "/organizations/:organization/shop/:shopId/products",
  shopProductNew: "/organizations/:organization/shop/:shopId/products/new",
  shopProductEdit:
    "/organizations/:organization/shop/:shopId/products/:id/edit",
  shopProductCategoryList:
    "/organizations/:organization/shop-product-categories",
  shopProductCategoryNew:
    "/organizations/:organization/shop-product-categories/new",
  shopProductCategoryEdit:
    "/organizations/:organization/shop-product-categories/:id/edit",
  shopProductAdditionsList:
    "/organizations/:organization/shop-product-additions",
  shopProductAdditionsNew:
    "/organizations/:organization/shop-product-additions/new",
  shopProductAdditionsEdit:
    "/organizations/:organization/shop-product-additions/:id/edit",
  appHome: "/organizations/:organization/app-home",
  homepageUpsertListEdit:
    "/organizations/:organization/app-home/homepages/:id/edit",
  homepageUpsertListNew: "/organizations/:organization/app-home/homepages/new",
  news: "/organizations/:organization/news",
  newsNew: "/organizations/:organization/news/new",
  newsEdit: "/organizations/:organization/news/:id/edit",
  ctasNew: "/organizations/:organization/app-home/ctas/new",
  ctasEdit: "/organizations/:organization/app-home/ctas/:id/edit",
  orderList: "/organizations/:organization/order",
  orderShow: "/organizations/:organization/order/:id/show",
  voucherTypesList: "/organizations/:organization/voucher-types",
  voucherTypesNew: "/organizations/:organization/voucher-types/new",
  voucherTypesEdit: "/organizations/:organization/voucher-types/:id/edit",
  organizations: "/organizations/:organization/organizations",
  organizationsEdit:
    "/organizations/:organization/organizations/:organizationToEdit/edit",
  punchCards: "/organizations/:organization/punch-cards",
  punchCardsNew: "/organizations/:organization/punch-cards/new",
  punchCardsEdit: "/organizations/:organization/punch-cards/:id/edit",
  deliveryrules: "/organizations/:organization/delivery-rules",
  challenges: "/organizations/:organization/challenges",
  challengesNew: "/organizations/:organization/challenges/new",
  challengesEdit: "/organizations/:organization/challenges/:id/edit",
  products: "/organizations/:organization/products",
  productEdit: "/organizations/:organization/products/:id",
  integrationsList: "/organizations/:organization/integrations",
  kitchenCategories: "/organizations/:organization/kitchen-categories",
  packingItems: "/organizations/:organization/packing-items",
  productTags: "/organizations/:organization/product-tags",
  autoCampaigns: "/organizations/:organization/auto-campaigns",
  campaigns: "/organizations/:organization/auto-campaigns/campaigns",
  campaignsEdit: "/organizations/:organization/auto-campaigns/campaigns/:id",
  campaignsNew: "/organizations/:organization/auto-campaigns/campaigns/new",
  productGroups: "/organizations/:organization/auto-campaigns/product-groups",
  productGroupEdit:
    "/organizations/:organization/auto-campaigns/product-groups/:id",
  productGroupNew:
    "/organizations/:organization/auto-campaigns/product-groups/new",
};

export const routes = {
  home: (organizationKey?: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.home.replace(":organization", organizationKey);
  },
  reports: (organizationKey?: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.reports.replace(":organization", organizationKey);
  },
  products: (organizationKey?: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.products.replace(":organization", organizationKey);
  },
  productEdit: (
    organizationKey?: string | undefined,
    id?: string | undefined
  ) => {
    return !organizationKey || !id
      ? "/"
      : routePatterns.productEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  integrationsList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.integrationsList.replace(
          ":organization",
          organizationKey
        );
  },
  menuList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.menuList.replace(":organization", organizationKey);
  },
  menuEdit: (organizationKey: string | undefined, id: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.menuEdit
          .replace(":organization", organizationKey)
          .replace(":id", id!);
  },
  menuEditRecommendedProducts: (
    organizationKey: string | undefined,
    id: string | undefined
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.menuEditRecommendedProducts
          .replace(":organization", organizationKey)
          .replace(":id", id!);
  },
  menuEditMenuAvailability: (
    organizationKey: string | undefined,
    id: string | undefined
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.menuEditMenuAvailability
          .replace(":organization", organizationKey)
          .replace(":id", id!);
  },
  menuEditCampaigns: (
    organizationKey: string | undefined,
    id: string | undefined
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.menuEditCampaigns
          .replace(":organization", organizationKey)
          .replace(":id", id!);
  },
  menuEditCampaignsAdd: (
    organizationKey: string | undefined,
    id: string | undefined
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.menuEditCampaignsAdd
          .replace(":organization", organizationKey)
          .replace(":id", id!);
  },
  userList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.userList.replace(":organization", organizationKey);
  },
  venueList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.venueList.replace(":organization", organizationKey);
  },
  venueNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.venueNew.replace(":organization", organizationKey);
  },
  venueEdit: (organizationKey: string | undefined, venueId: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.venueEdit
          .replace(":organization", organizationKey)
          .replace(":id", venueId);
  },
  userEventLogList: (
    organizationKey: string | undefined,
    userId: number | string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.userEventLogList
          .replace(":organization", organizationKey)
          .replace(":id", "" + userId);
  },
  userProfile: (
    organizationKey: string | undefined,
    userId?: number | string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.userProfile
          .replace(":organization", organizationKey)
          .replace(":id", "" + userId);
  },
  organizationUISettingsEdit: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.organizationUISettingsEdit.replace(
          ":organization",
          organizationKey
        );
  },
  graphs: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.analytics.replace(":organization", organizationKey);
  },
  beaconStatusView: (
    organizationKey: OrganizationKeyName | undefined,
    id: string,
    idNfc: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.beaconStatusView
          .replace(":organization", organizationKey)
          .replace(":id", id)
          .replace(":idNfc", idNfc);
  },
  beaconStatus: (organizationKey: OrganizationKeyName | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.beaconStatus.replace(":organization", organizationKey);
  },
  promotionCodeList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.promotionCodeList.replace(
          ":organization",
          organizationKey
        );
  },
  promotionCodeAdd: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.promotionCodeAdd.replace(
          ":organization",
          organizationKey
        );
  },
  promotionCodeEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.promotionCodeEdit
          .replace(":id", id)
          .replace(":organization", organizationKey);
  },
  organizationEventLog: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.organizationEventLog.replace(
          ":organization",
          organizationKey
        );
  },
  emailTemplates: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.emailTemplates.replace(":organization", organizationKey);
  },
  emailTemplateEdit: (
    organizationKey: string | undefined,
    emailTemplateId: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.emailTemplateEdit
          .replace(":organization", organizationKey)
          .replace(":id", emailTemplateId);
  },
  emailTemplateNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.emailTemplateNew.replace(
          ":organization",
          organizationKey
        );
  },
  adminUserList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.adminUserList.replace(":organization", organizationKey);
  },
  adminUserAdd: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.adminUserAdd.replace(":organization", organizationKey);
  },
  adminUserEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.adminUserEdit
          .replace(":id", id)
          .replace(":organization", organizationKey);
  },
  memberCardTypes: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.memberCardTypes.replace(":organization", organizationKey);
  },
  memberCardTypeEdit: (
    organizationKey: string | undefined,
    memberCardTypeId: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.memberCardTypeEdit
          .replace(":organization", organizationKey)
          .replace(":id", memberCardTypeId);
  },
  memberCardTypeNew: (
    organizationKey: string | undefined,
    emailTemplateId: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.memberCardTypeNew.replace(
          ":organization",
          organizationKey
        );
  },
  partnershipList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.partnershipList.replace(":organization", organizationKey);
  },
  partnershipAdd: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.partnershipAdd.replace(":organization", organizationKey);
  },
  partnershipEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.partnershipEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  shopList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopList.replace(":organization", organizationKey);
  },
  shopNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopNew.replace(":organization", organizationKey);
  },
  shopNewWithVenue: (organizationKey: string | undefined, venueId: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopNewWithVenue
          .replace(":organization", organizationKey)
          .replace(":venueId", venueId);
  },
  shopEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  shopProductListForShop: (
    organizationKey: string | undefined,
    shopId: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductListForShop
          .replace(":organization", organizationKey)
          .replace(":shopId", shopId);
  },
  shopProductNew: (organizationKey: string | undefined, shopId: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductNew
          .replace(":organization", organizationKey)
          .replace(":shopId", shopId);
  },
  shopProductCategoryList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductCategoryList.replace(
          ":organization",
          organizationKey
        );
  },
  shopProductCategoryEdit: (
    organizationKey: string | undefined,
    id: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductCategoryEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  shopProductCategoryNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductCategoryNew.replace(
          ":organization",
          organizationKey
        );
  },
  shopProductAdditionsList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductAdditionsList.replace(
          ":organization",
          organizationKey
        );
  },
  shopProductAdditionsNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductAdditionsNew.replace(
          ":organization",
          organizationKey
        );
  },
  shopProductAdditionsEdit: (
    organizationKey: string | undefined,
    id: string
  ) => {
    return !organizationKey
      ? "/"
      : routePatterns.shopProductAdditionsEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  appHome: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.appHome.replace(":organization", organizationKey);
  },
  homepageUpsertListEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.homepageUpsertListEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  homepageUpsertListNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.homepageUpsertListNew.replace(
          ":organization",
          organizationKey
        );
  },
  news: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.news.replace(":organization", organizationKey);
  },
  newsNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.newsNew.replace(":organization", organizationKey);
  },
  newsEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.newsEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  ctasNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.ctasNew.replace(":organization", organizationKey);
  },
  ctasEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.ctasEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  orderList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.orderList.replace(":organization", organizationKey);
  },
  orderShow: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.orderShow
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  voucherTypeList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.voucherTypesList.replace(
          ":organization",
          organizationKey
        );
  },
  voucherTypeEdit: (organizationKey: string | undefined, id: string) => {
    return !organizationKey
      ? "/"
      : routePatterns.voucherTypesEdit
          .replace(":organization", organizationKey)
          .replace(":id", id);
  },
  voucherTypeNew: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.voucherTypesNew.replace(":organization", organizationKey);
  },
  organizationsList: (organizationKey: string | undefined) => {
    return !organizationKey
      ? "/"
      : routePatterns.organizations.replace(":organization", organizationKey);
  },
  organizationEdit: (
    organizationKey: string | undefined,
    organizationToEditKey: string | undefined
  ) => {
    return !organizationKey || !organizationToEditKey
      ? "/"
      : routePatterns.organizationsEdit
          .replace(":organization", organizationKey)
          .replace(":organizationToEdit", organizationToEditKey);
  },
  punchCards: (organizationKeyName: OrganizationKeyName) => {
    return routePatterns.punchCards.replace(
      ":organization",
      organizationKeyName
    );
  },
  punchCardsNew: (organizationKeyName: OrganizationKeyName) => {
    return routePatterns.punchCardsNew.replace(
      ":organization",
      organizationKeyName
    );
  },
  punchCardsEdit: (organizationKeyName: OrganizationKeyName, id: string) => {
    return routePatterns.punchCardsEdit
      .replace(":organization", organizationKeyName)
      .replace(":id", id);
  },
  deliveryRules: (organizationKeyName: OrganizationKeyName) => {
    return routePatterns.deliveryrules.replace(
      ":organization",
      organizationKeyName
    );
  },
  challengesEdit: (organizationKeyName: OrganizationKeyName, id: string) => {
    return routePatterns.challengesEdit
      .replace(":organization", organizationKeyName)
      .replace(":id", id);
  },
  challenges: (organizationKeyName: OrganizationKeyName) => {
    return routePatterns.challenges.replace(
      ":organization",
      organizationKeyName
    );
  },
  challengesNew: (organizationKeyName: OrganizationKeyName) => {
    return routePatterns.challengesNew.replace(
      ":organization",
      organizationKeyName
    );
  },
  kitchenCategories: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.kitchenCategories.replace(
          ":organization",
          organizationKeyName
        );
  },
  packingItems: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.packingItems.replace(
          ":organization",
          organizationKeyName
        );
  },

  productTags: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.productTags.replace(":organization", organizationKeyName);
  },

  autoCampaigns: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.autoCampaigns.replace(
          ":organization",
          organizationKeyName
        );
  },

  campaigns: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.campaigns.replace(":organization", organizationKeyName);
  },

  campaignsEdit: (organizationKeyName?: OrganizationKeyName, id?: string) => {
    return !organizationKeyName || !id
      ? "/"
      : routePatterns.campaignsEdit
          .replace(":organization", organizationKeyName)
          .replace(":id", id);
  },

  campaignsNew: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.campaignsNew.replace(
          ":organization",
          organizationKeyName
        );
  },

  productGroups: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.productGroups.replace(
          ":organization",
          organizationKeyName
        );
  },

  productGroupEdit: (
    organizationKeyName?: OrganizationKeyName,
    id?: string
  ) => {
    return !organizationKeyName || !id
      ? "/"
      : routePatterns.productGroupEdit
          .replace(":organization", organizationKeyName)
          .replace(":id", id);
  },

  productGroupNew: (organizationKeyName?: OrganizationKeyName) => {
    return !organizationKeyName
      ? "/"
      : routePatterns.productGroupNew.replace(
          ":organization",
          organizationKeyName
        );
  },
};
